/* ═════════════════════════════════════════════════════════════════════════
   TONA · AGREGADO RÁPIDO (PR-25)
   El «+» de las tarjetas abre la ventana #tona-ar: primero el color, después el talle, y agrega al
   carrito la variante exacta. Marcado, estilos y el formato de los datos: snippets/tona-agregado-rapido.liquid.
   Ajustes: Ajustes del tema → «Tona - Agregado rápido» (llegan como data-* en la ventana).

   Lo que no se ve leyendo el código:
   · Shopify ACEPTA en /cart/add.js una variante agotada (medido en el PR-23: responde 200), así que
     la barrera es este archivo: nada que no esté disponible en los datos se pide.
   · El carrito y el contador se enteran con el mismo evento que usa el formulario de la ficha
     (CartAddEvent de @theme/events), no rearmando el header a mano como hacía la hoja vieja.
   · Los radios NO llevan la clase variant-option, y eso es a propósito: snippets/tona-talle-de-entrada.liquid
     (antes default-size-L.liquid) le hace clic solo al talle de entrada de la FICHA buscando
     `variant-picker .variant-option input[type=radio]`. Si estos radios llevaran esa clase, el talle
     de la ficha se metería a elegir adentro de esta ventana. No se la pongas.

   · PR-98 (21/09) · LA VENTANA ABRE CON EL COLOR Y EL TALLE YA ELEGIDOS, con los ajustes
     «Abrir con todo elegido» (data-auto-color, data-auto-talle, data-orden-talles, data-orden-resto).
     No se tocó la lógica de esconder los pasos siguientes: con el color elegido, el grupo del talle se
     muestra solo. La preselección vive en armar() → autoElegir().

   TONA · LAS PASTILLAS DE LAS TARJETAS (PR-63, F15) · la segunda mitad de este archivo, iniciarTarjetas().
   Vive acá y no en un <script> de snippets/product-card.liquid porque ese se imprimía una vez por
   tarjeta; este archivo se carga una sola vez por página y escucha a TODAS las tarjetas por delegación,
   también a las que llegan después por fetch (las pestañas de la ficha, el scroll infinito).
   Ajustes: Ajustes del tema → «Tona - Pastillas de color» (llegan como data-* en cada tarjeta).
   ═════════════════════════════════════════════════════════════════════════ */
import { CartAddEvent } from '@theme/events';

/* [versión, id, título, foto, opciones, variantes, fotos] — el orden está en el snippet.
   Lo leen la ventana del «+» y las tarjetas. */
function leerDatos(mas) {
  const d = JSON.parse(mas.getAttribute('data-tona-ar') || '');
  if (!Array.isArray(d) || d[0] !== 1) throw new Error('datos del agregado rápido con otro formato');
  const fotos = new Map();
  for (const f of d[6] || []) if (f[0]) fotos.set(f[0], f[1]);
  return {
    id: d[1],
    titulo: d[2],
    foto: d[3],
    opciones: (d[4] || []).map((o) => ({
      nombre: o[0],
      esColor: o[1] === 1,
      valores: (o[2] || []).map((v) => ({ nombre: v[0], muestra: v[1] || '' })),
    })),
    variantes: (d[5] || []).map((v) => ({
      id: v[0],
      valores: v[1] || [],
      disponible: v[2] === 1,
      pocas: v[3] === 1,
      foto: fotos.get(v[4]) || '',
    })),
  };
}

/* El color que la tarjeta tiene ELEGIDO (lo escribe iniciarTarjetas() al tocar una pastilla).
   Sin elegir no hay nada: el «+» abre como siempre. */
function pedidoDeLaTarjeta(mas) {
  const tarjeta = mas.closest('product-card');
  const color = tarjeta ? tarjeta.getAttribute('data-tona-color') : null;
  return color ? { color } : null;
}

/* Agrega una variante al carrito y avisa con el mismo evento que usa el formulario de la ficha
   (CartAddEvent): así se actualizan el contador y el cajón del carrito. Lo usan la ventana del «+» y
   la fila de talles de las tarjetas (PR-75). Tira un error si Shopify no la agregó.

   PR-81 · el tercer parámetro es EL ELEMENTO DE ORIGEN (el «+» de la tarjeta o la propia tarjeta).
   El evento se dispara desde ahí y no desde `document` para que burbujee por la tarjeta y la
   sección: así snippets/cart-auto-open.liquid puede leer el `data-tona-abrir-carrito` del lugar de
   donde salió el agregado. Igual llega a `document` (bubbles: true), así que el contador y el cajón
   se enteran como siempre. Si el origen ya no está en la página, se dispara desde `document`. */
async function agregarAlCarrito(varianteId, productoId, origen) {
  const rutas = (window.Theme && window.Theme.routes) || {};

  /* PR-81b · AVISO DE QUE EMPIEZA. El carrito se abre con esto y no cuando contesta el servidor:
     medido el 20/09, leer el carrito tarda 468 a 815 ms y dibujar el cajón 409 a 922 ms, y este
     camino hace DOS pedidos, así que esperar la respuesta son más de mil milisegundos mirando una
     pantalla que no reacciona. El cajón abre ya y se llena cuando llega el dato.
     Burbujea desde el origen, igual que el evento de terminado, para que el lugar decida si abre. */
  const avisar = origen && origen.isConnected ? origen : document;
  avisar.dispatchEvent(new CustomEvent('tona:agregando', { bubbles: true }));
  const secciones = Array.from(document.querySelectorAll('cart-items-component'))
    .map((c) => c.dataset.sectionId)
    .filter(Boolean);
  const respuesta = await fetch((rutas.cart_add_url || '/cart/add') + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items: [{ id: varianteId, quantity: 1 }], sections: secciones.join(',') }),
  });
  const datos = await respuesta.json().catch(() => ({}));
  if (!respuesta.ok || datos.status) throw new Error(datos.description || datos.message || String(respuesta.status));

  /* El contador del carrito recibe el total real. PR-84: ese número ya viene DENTRO de las
     secciones que devolvió el mismo pedido de agregar (el tema lo esconde en `[ref="cartItemCount"]`,
     igual que hace assets/component-cart-items.js), así que no hace falta pedir /cart.js aparte.
     Medido el 20/09: ese segundo pedido tardaba 468 a 815 ms, o sea que el producto tardaba eso de
     más en aparecer en el cajón. Si por lo que sea no se puede leer, se suma 1 como hace la ficha. */
  let detalle = { source: 'tona-agregado-rapido', itemCount: 1 };
  try {
    const htmlSeccion = datos.sections && (datos.sections[secciones[0]] || Object.values(datos.sections)[0]);
    const cuenta = htmlSeccion
      ? new DOMParser().parseFromString(htmlSeccion, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
      : null;
    const n = cuenta ? parseInt(cuenta, 10) : NaN;
    if (!Number.isNaN(n)) detalle = { source: 'tona-agregado-rapido', itemCount: n };
  } catch (e) {
    /* se queda con el +1 */
  }
  const desde = origen && origen.isConnected ? origen : document;
  desde.dispatchEvent(
    new CartAddEvent({}, String(varianteId), {
      ...detalle,
      productId: String(productoId),
      variantId: String(varianteId),
      sections: datos.sections,
    })
  );
}

/* El aviso de abajo («Producto añadido»). Sin texto, el del ajuste. */
let timerToast = 0;
function mostrarToast(texto) {
  const toast = document.getElementById('tona-ar-toast');
  const mensaje = texto || (toast ? toast.dataset.texto : '');
  if (!toast || !mensaje) return;
  clearTimeout(timerToast);
  toast.hidden = false;
  toast.textContent = mensaje;
  void toast.offsetWidth;
  toast.classList.add('tona-ar-toast--visible');
  timerToast = setTimeout(() => {
    toast.classList.remove('tona-ar-toast--visible');
    timerToast = setTimeout(() => {
      toast.hidden = true;
    }, 250);
  }, 2200);
}

/* «Qué pasa después de agregar», en Tona - Agregado rápido: abrir el carrito lateral o el aviso.

   PR-81 · el que abre el carrito ahora es snippets/cart-auto-open.liquid, que ya corrió con el
   CartAddEvent de arriba y deja escrita la marca `window.tonaCarritoAbierto`. Si el carrito se va a
   abrir, el aviso sobra y no se muestra. La marca se lee en vez de mirar `dialog[open]` porque el
   cajón se abre dentro de un requestAnimationFrame y todavía no está abierto en este momento. */
function despuesDeAgregar() {
  if (Date.now() - (window.tonaCarritoAbierto || 0) < 1000) return;
  const ventana = document.getElementById('tona-ar');
  const abrirCarrito = ventana && ventana.dataset.despues === 'carrito';
  const cajon = abrirCarrito ? document.querySelector('cart-drawer-component') : null;
  if (cajon && typeof cajon.open === 'function') cajon.open();
  else mostrarToast();
}

/* abrir() de la ventana del «+», para la fila de talles de las tarjetas (PR-75). La define iniciar(). */
let abrirVentana = null;

const dialogo = document.getElementById('tona-ar');

if (dialogo && !dialogo.tonaArListo) {
  dialogo.tonaArListo = true;
  iniciar(dialogo);
}

if (!window.tonaTarjetasListas) {
  window.tonaTarjetasListas = true;
  iniciarTarjetas();
}

function iniciar(dialogo) {
  const cfg = dialogo.dataset;
  const foto = dialogo.querySelector('.tona-ar__foto');
  const titulo = dialogo.querySelector('.tona-ar__titulo');
  const pasos = dialogo.querySelector('.tona-ar__pasos');
  const aviso = dialogo.querySelector('.tona-ar__aviso');
  const boton = dialogo.querySelector('.tona-ar__agregar');
  const textoBoton = boton ? boton.querySelector('.tona-boton__pildora') : null;
  const error = dialogo.querySelector('.tona-ar__error');
  const cerrarBoton = dialogo.querySelector('.tona-ar__cerrar');
  if (!foto || !titulo || !pasos || !aviso || !boton || !textoBoton || !error) return;

  let producto = null; // los datos del «+» que se tocó
  let orden = []; // índices de las opciones en el orden de los pasos: el color primero
  let elegidos = []; // el valor elegido de cada opción (null = falta)
  let disparador = null; // el «+», para devolverle el foco al cerrar
  let devolverFoco = true;
  let enviando = false;
  let scrollGuardado = 0;
  let bloqueado = false;
  let serie = 0;

  const reducido = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(etiqueta, clase, texto) {
    const nodo = document.createElement(etiqueta);
    if (clase) nodo.className = clase;
    if (texto != null) nodo.textContent = texto;
    return nodo;
  }

  /* Los valores que se ofrecen. Regla de Vika, la misma de la ficha (snippets/variant-main-picker.liquid)
     y de las tarjetas: si algún color tiene foto, los colores sin foto no se muestran. */
  function valoresVisibles(k) {
    const op = producto.opciones[k];
    let valores = op.valores.filter((v) => producto.variantes.some((x) => x.valores[k] === v.nombre));
    if (op.esColor) {
      const conFoto = valores.filter((v) => producto.variantes.some((x) => x.valores[k] === v.nombre && x.foto));
      if (conFoto.length) valores = conFoto;
    }
    return valores;
  }

  /* Un valor se puede elegir si hay una variante disponible con él y con lo ya elegido en los pasos anteriores */
  function disponible(k, valor) {
    const antes = orden.slice(0, orden.indexOf(k));
    return producto.variantes.some(
      (v) => v.disponible && v.valores[k] === valor && antes.every((j) => elegidos[j] == null || v.valores[j] === elegidos[j])
    );
  }

  /* PR-98 (21/09) · ABRIR CON TODO ELEGIDO.
     Pedido de Tona: que la ventana abra con un color y un talle ya marcados, así comprar es un solo
     clic. La lista de talles preferidos sale del ajuste «Orden de preferencia de talles» (L, M de
     fábrica, que es lo que la tienda venía haciendo a mano en la ficha).

     La comparación de talles es FLOJA (sin mayúsculas ni espacios): «l», «L» y « L » son el mismo
     talle. A propósito NO hay equivalencias inteligentes (L ≠ Large, 2XL ≠ XXL): si Tona quiere las
     dos formas, escribe las dos en la lista. Adivinar acá haría que la ventana eligiera un talle que
     nadie pidió y nadie sabría por qué. */
  const tallesPreferidos = (cfg.ordenTalles || '')
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  /* Qué elige solo el paso k, si no vino nada de la tarjeta. Devuelve null = queda sin elegir.
     ⚠️ Se llama DENTRO del recorrido de `orden`, que arranca por el color: así `disponible()` ya
     tiene el color como filtro y nunca elige un talle que ese color no tiene. */
  function autoElegir(k, valores) {
    if (producto.opciones[k].esColor) {
      if (cfg.autoColor !== '1') return null;
      const v = valores.find((x) => disponible(k, x.nombre));
      return v ? v.nombre : null;
    }
    if (cfg.autoTalle !== '1') return null;
    for (const preferido of tallesPreferidos) {
      const v = valores.find((x) => x.nombre.trim().toLowerCase() === preferido && disponible(k, x.nombre));
      if (v) return v.nombre;
    }
    if (cfg.ordenResto !== '1') return null;
    const v = valores.find((x) => disponible(k, x.nombre));
    return v ? v.nombre : null;
  }

  /* Un valor EXISTE si hay una variante con él y con lo ya elegido en los pasos anteriores, esté o no
     disponible. Lo que no existe no se muestra (con un color elegido, solo sus talles); lo que existe y
     no está disponible se muestra tachado. PR-63. */
  function existe(k, valor) {
    const antes = orden.slice(0, orden.indexOf(k));
    return producto.variantes.some(
      (v) => v.valores[k] === valor && antes.every((j) => elegidos[j] == null || v.valores[j] === elegidos[j])
    );
  }

  function varianteElegida() {
    if (!producto || elegidos.some((e) => e == null)) return null;
    return producto.variantes.find((v) => v.valores.every((val, k) => val === elegidos[k])) || null;
  }

  /* Lo que llega ya elegido de la tarjeta ({ color, talle }), pasado a índices de opción */
  function porIndice(pedido) {
    const salida = {};
    if (!pedido) return salida;
    const iColor = producto.opciones.findIndex((o) => o.esColor);
    if (pedido.color != null && iColor >= 0) salida[iColor] = pedido.color;
    if (pedido.talle != null) {
      const otras = producto.opciones.map((_, i) => i).filter((i) => i !== iColor);
      if (otras.length === 1) salida[otras[0]] = pedido.talle;
    }
    return salida;
  }

  function fotoDeColor(k, valor) {
    const v = producto.variantes.find((x) => x.valores[k] === valor && x.foto);
    return v ? v.foto : '';
  }

  function ponerFoto(url) {
    if (url) {
      if (foto.getAttribute('src') !== url) foto.src = url;
      foto.hidden = false;
    } else {
      foto.hidden = true;
    }
  }

  function ponerTextoBoton(texto) {
    textoBoton.textContent = texto || cfg.textoBoton || '';
  }

  function pintarMuestra(nodo, k, v, clase) {
    const deColor = fotoDeColor(k, v.nombre);
    if (clase === 'foto' && deColor) {
      nodo.style.backgroundImage = 'url("' + deColor + '")';
    } else if (v.muestra.indexOf('url(') === 0) {
      nodo.style.backgroundImage = v.muestra;
    } else if (v.muestra) {
      nodo.style.backgroundColor = v.muestra;
    } else if (deColor) {
      nodo.style.backgroundImage = 'url("' + deColor + '")';
    }
  }

  function armarOpcion(k, v, esColor, lista, nombreGrupo) {
    const item = el('label', 'tona-ar__item');
    const radio = el('input', 'tona-ar__radio');
    radio.type = 'radio';
    radio.name = nombreGrupo;
    radio.value = v.nombre;
    radio.addEventListener('change', () => {
      if (radio.checked) elegir(k, v.nombre);
    });
    item.append(radio);

    if (esColor) {
      const estilo = cfg.colorEstilo === 'cuadraditos' ? 'cuadradito' : cfg.colorEstilo === 'fotos' ? 'foto' : 'puntito';
      const opcion = el(
        'tona-ar-opcion',
        'tona-ar__opcion tona-ar__opcion--' + (lista ? 'fila' : 'muestra') + ' tona-ar__opcion--' + estilo
      );
      const muestra = el('tona-ar-muestra', 'tona-ar__muestra tona-ar__muestra--' + estilo);
      pintarMuestra(muestra, k, v, estilo);
      opcion.append(muestra, el('tona-ar-t', lista ? 'tona-ar__nombre' : 'tona-ar__sr', v.nombre));
      opcion.append(el('tona-ar-t', lista ? 'tona-ar__estado tona-ar__agotado' : 'tona-ar__sr tona-ar__agotado', cfg.textoAgotado || ''));
      item.append(opcion);
    } else {
      const opcion = el('tona-ar-opcion', 'tona-ar__opcion tona-ar__opcion--talle', v.nombre);
      opcion.append(el('tona-ar-t', 'tona-ar__sr tona-ar__agotado', cfg.textoAgotado || ''));
      item.append(opcion);
    }
    return item;
  }

  function flecha() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'tona-ar__flecha');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M6 9l6 6 6-6');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  function armarGrupo(k, valores) {
    const op = producto.opciones[k];
    const esColor = op.esColor && k === orden[0];
    const rotulo = (esColor ? cfg.rotuloColor : cfg.rotuloTalle) || op.nombre;
    const base = 'tona-ar-' + serie + '-' + k;

    if (!esColor && cfg.talleEstilo === 'desplegable') {
      const grupo = el('div', 'tona-ar__grupo');
      grupo.dataset.k = String(k);
      const label = el('label', 'tona-ar__rotulo');
      label.htmlFor = base + '-select';
      label.append(el('tona-ar-t', 'tona-ar__rotulo-texto', rotulo));
      const caja = el('tona-ar-desplegable', 'tona-ar__desplegable');
      const select = el('select', 'tona-ar__select');
      select.id = base + '-select';
      const vacio = new Option(rotulo, '');
      vacio.disabled = true;
      vacio.hidden = true;
      select.append(vacio);
      for (const v of valores) select.append(new Option(v.nombre, v.nombre));
      select.addEventListener('change', () => elegir(k, select.value));
      caja.append(select, flecha());
      grupo.append(label, caja);
      return grupo;
    }

    const grupo = el('fieldset', 'tona-ar__grupo');
    grupo.dataset.k = String(k);
    const leyenda = el('legend', 'tona-ar__rotulo');
    leyenda.id = base + '-rotulo';
    leyenda.append(el('tona-ar-t', 'tona-ar__rotulo-texto', rotulo));
    if (esColor && cfg.colorNombre === 'true') leyenda.append(el('tona-ar-t', 'tona-ar__rotulo-valor'));
    const lista = esColor && cfg.colorFormato === 'lista';
    const caja = el('div', 'tona-ar__opciones' + (lista ? ' tona-ar__opciones--lista' : '') + (esColor ? '' : ' tona-ar__opciones--talles'));
    caja.setAttribute('role', 'radiogroup');
    caja.setAttribute('aria-labelledby', leyenda.id);
    for (const v of valores) caja.append(armarOpcion(k, v, esColor, lista, base));
    grupo.append(leyenda, caja);
    return grupo;
  }

  /* pedido: lo que llega ya elegido de la tarjeta, por índice de opción (porIndice). Se toma solo si se
     ofrece y está disponible con lo anterior; si no, ese paso queda para elegir como siempre.
     PR-98: si después de eso el paso sigue vacío, lo llena autoElegir(). El orden de las tres reglas es
     el que importa: LO QUE VINO DE LA TARJETA MANDA SIEMPRE sobre lo automático. */
  function armar(pedido) {
    serie += 1;
    const n = producto.opciones.length;
    elegidos = new Array(n).fill(null);
    orden = Array.from({ length: n }, (_, i) => i);
    const iColor = producto.opciones.findIndex((o) => o.esColor);
    if (iColor > 0) {
      orden.splice(iColor, 1);
      orden.unshift(iColor);
    }
    pasos.replaceChildren();

    if (producto.variantes.length === 1) {
      // Una sola variante: sin pasos, queda solo el botón
      elegidos = producto.variantes[0].valores.slice();
    } else {
      for (const k of orden) {
        const valores = valoresVisibles(k);
        const quiere = pedido ? pedido[k] : null;
        if (quiere != null && valores.some((v) => v.nombre === quiere) && disponible(k, quiere)) elegidos[k] = quiere;
        else if (valores.length === 1 && disponible(k, valores[0].nombre)) elegidos[k] = valores[0].nombre;
        else elegidos[k] = autoElegir(k, valores);
        pasos.append(armarGrupo(k, valores));
      }
    }

    titulo.textContent = producto.titulo;
    error.hidden = true;
    boton.removeAttribute('aria-busy');
    ponerTextoBoton(cfg.textoBoton);
    actualizar();
  }

  function elegir(k, valor) {
    elegidos[k] = valor || null;
    // Lo que se eligió en los pasos siguientes y ya no existe con este valor, se suelta
    for (const j of orden.slice(orden.indexOf(k) + 1)) {
      if (elegidos[j] != null && !disponible(j, elegidos[j])) elegidos[j] = null;
    }
    error.hidden = true;
    actualizar();
  }

  function actualizar() {
    let anterioresListos = true;
    let fotoActual = producto.foto;

    for (const k of orden) {
      const grupo = pasos.querySelector('[data-k="' + k + '"]');
      if (!grupo) continue;
      grupo.hidden = !anterioresListos;
      grupo.classList.remove('tona-ar__grupo--falta');

      const select = grupo.querySelector('select');
      if (select) {
        for (const o of select.options) {
          if (!o.value) continue;
          const ok = disponible(k, o.value);
          o.disabled = !ok;
          o.hidden = !existe(k, o.value);
          o.textContent = ok || !cfg.textoAgotado ? o.value : o.value + ' · ' + cfg.textoAgotado;
        }
        select.value = elegidos[k] == null ? '' : elegidos[k];
      } else {
        for (const radio of grupo.querySelectorAll('.tona-ar__radio')) {
          const ok = disponible(k, radio.value);
          radio.disabled = !ok;
          radio.checked = elegidos[k] === radio.value;
          radio.parentElement.hidden = !existe(k, radio.value);
          const agotado = radio.parentElement.querySelector('.tona-ar__agotado');
          if (agotado) agotado.hidden = ok || !cfg.textoAgotado;
        }
        const valor = grupo.querySelector('.tona-ar__rotulo-valor');
        if (valor) valor.textContent = elegidos[k] == null ? '' : ' — ' + elegidos[k];
      }

      if (producto.opciones[k].esColor && k === orden[0] && elegidos[k] != null) {
        fotoActual = fotoDeColor(k, elegidos[k]) || fotoActual;
      }
      if (elegidos[k] == null) anterioresListos = false;
    }

    const v = varianteElegida();
    if (v && v.foto) fotoActual = v.foto;
    ponerFoto(fotoActual);
    aviso.hidden = !(v && v.disponible && v.pocas);
    boton.setAttribute('aria-disabled', v && v.disponible ? 'false' : 'true');
  }

  function marcarFalta() {
    const k = orden.find((j) => elegidos[j] == null);
    if (k == null) return;
    const grupo = pasos.querySelector('[data-k="' + k + '"]');
    if (!grupo || grupo.hidden) return;
    grupo.classList.add('tona-ar__grupo--falta');
    const destino = grupo.querySelector('.tona-ar__radio:not(:disabled)') || grupo.querySelector('select');
    if (destino) destino.focus();
  }

  /* El foco al abrir: el primer paso que falta elegir. PR-98: con todo ya elegido no falta ninguno, y
     entonces va al botón de agregar, que es lo único que queda por hacer. El botón se apaga con
     aria-disabled y no con disabled, así que sigue recibiendo foco; el de cerrar queda de última red
     para que el foco no se pierda en el <body> (un <dialog> modal sin foco adentro lo deja ahí). */
  function enfocarInicio() {
    const grupo = Array.from(pasos.querySelectorAll('.tona-ar__grupo')).find(
      (g) => !g.hidden && elegidos[Number(g.dataset.k)] == null
    );
    const enElGrupo = grupo
      ? grupo.querySelector('.tona-ar__radio:not(:disabled)') || grupo.querySelector('select')
      : null;
    const destino = enElGrupo || boton || cerrarBoton;
    if (destino) destino.focus({ preventScroll: true });
  }

  function bloquearScroll() {
    if (bloqueado) return;
    bloqueado = true;
    scrollGuardado = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollGuardado + 'px';
  }

  function desbloquearScroll() {
    if (!bloqueado) return;
    bloqueado = false;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollGuardado, behavior: 'instant' });
  }

  /* pedido: { color, talle } ya elegidos en la tarjeta (PR-63), o null */
  function abrir(mas, pedido) {
    let datos;
    try {
      datos = leerDatos(mas);
    } catch (e) {
      console.error('Tona - Agregado rápido:', e);
      return;
    }
    producto = datos;
    disparador = mas;
    armar(porIndice(pedido));
    bloquearScroll();
    if (!dialogo.open) dialogo.showModal();
    void dialogo.offsetWidth;
    dialogo.classList.add('tona-ar--visible');
    enfocarInicio();
  }

  function cerrar(conFoco) {
    return new Promise((resolver) => {
      if (!dialogo.open) {
        resolver();
        return;
      }
      devolverFoco = conFoco !== false;
      dialogo.classList.remove('tona-ar--visible');
      const fin = () => {
        if (dialogo.open) dialogo.close();
        resolver();
      };
      if (reducido()) fin();
      else setTimeout(fin, 300);
    });
  }

  async function agregar() {
    if (enviando) return;
    const v = varianteElegida();
    if (!v || !v.disponible) {
      marcarFalta();
      return;
    }
    enviando = true;
    boton.setAttribute('aria-busy', 'true');
    ponerTextoBoton(cfg.textoAgregando);
    error.hidden = true;

    try {
      /* El origen es el «+» que abrió la ventana: vive dentro de la tarjeta y de la sección, que es
         donde snippets/cart-auto-open.liquid busca el interruptor (PR-81). */
      await agregarAlCarrito(v.id, producto.id, disparador);
      await cerrar(cfg.despues !== 'carrito');
      despuesDeAgregar();
    } catch (e) {
      console.warn('Tona - Agregado rápido: no se pudo agregar', e);
      error.hidden = false;
    } finally {
      enviando = false;
      boton.removeAttribute('aria-busy');
      ponerTextoBoton(cfg.textoBoton);
    }
  }

  document.addEventListener('click', (e) => {
    const mas = e.target instanceof Element ? e.target.closest('.tona-ar-mas') : null;
    if (!mas) return;
    e.preventDefault();
    abrir(mas, pedidoDeLaTarjeta(mas));
  });
  abrirVentana = abrir;

  boton.addEventListener('click', agregar);
  if (cerrarBoton) cerrarBoton.addEventListener('click', () => cerrar(true));

  // Esc: se cierra con la misma animación. Si el navegador lo cierra igual, el 'close' limpia.
  dialogo.addEventListener('cancel', (e) => {
    e.preventDefault();
    cerrar(true);
  });

  // Tab da la vuelta adentro de la ventana. Un <dialog> modal deja salir el foco al navegador después
  // del último elemento (medido: al segundo recorrido quedaba en el body), así que se cierra el círculo a mano.
  function tabulables() {
    const lista = [];
    const grupos = new Set();
    for (const e of dialogo.querySelectorAll('button, select, input, [href], [tabindex]')) {
      if (e.disabled || e.closest('[hidden]') || e.getAttribute('tabindex') === '-1') continue;
      if (e.type === 'radio') {
        if (grupos.has(e.name)) continue;
        grupos.add(e.name);
        const radios = Array.from(dialogo.querySelectorAll('input[type="radio"]')).filter((r) => r.name === e.name && !r.disabled);
        const tabulable = radios.find((r) => r.checked) || radios[0];
        if (tabulable) lista.push(tabulable);
        continue;
      }
      lista.push(e);
    }
    return lista;
  }

  dialogo.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const lista = tabulables();
    if (!lista.length) return;
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (!e.shiftKey && (document.activeElement === ultimo || !dialogo.contains(document.activeElement))) {
      e.preventDefault();
      primero.focus();
    } else if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    }
  });

  // Tocar afuera de la ventana (el fondo oscuro) la cierra
  dialogo.addEventListener('click', (e) => {
    if (e.target === dialogo) cerrar(true);
  });

  dialogo.addEventListener('close', () => {
    dialogo.classList.remove('tona-ar--visible');
    desbloquearScroll();
    if (devolverFoco && disparador && disparador.isConnected) disparador.focus({ preventScroll: true });
    devolverFoco = true;
  });

  window.addEventListener('pagehide', () => {
    if (dialogo.open) dialogo.close();
  });
}

/* ═════════════════════════════════════════════════════════════════════════
   LAS PASTILLAS DE LAS TARJETAS (PR-63)
   Las pastillas las dibuja snippets/product-card.liquid. Cada una trae:
     data-color-name          el nombre del color
     data-swatch-hover-image  la foto de ese color que se muestra (URL sin ancho)
     data-tona-foto-siguiente la foto siguiente de ese color (la que Horizon pone al pasar el mouse por la foto).
                              No se llama data-tona-foto-2: un guion seguido de un número no pasa a mayúscula
                              en dataset, y dataset.tonaFoto2 daba vacío.
     data-tona-url            la ficha de ese color (solo en «Elegir el color»)
   y el contenedor data-tona-tocar («elegir» o «llevar»). Con «elegir» la pastilla es un <button>; con
   «llevar», el <a> de siempre, y de eso se encarga el navegador.

   Lo que no se ve leyendo el código:
   · La foto que se cambia es un <img> de la galería de Horizon (slideshow-slide). Se le pone el mismo srcset
     que usa la tarjeta: la foto de 500 px que ponía el script viejo se veía blanda en pantallas retina.
   · Al elegir un color se cambian DOS fotos: la primera diapositiva y la segunda. Horizon pasa a la segunda
     al pasar el mouse por la foto (show_second_image_on_hover), así que sin cambiarla, al subir a la foto
     para tocar un talle aparecía la segunda foto del color de arranque.
   · Todo el estado va en atributos (data-tona-color en la tarjeta, aria-pressed, data-tona-elegida en cada
     foto) y no en propiedades de JS: cuando Horizon rearma la grilla (filtros), el morph vuelve los
     atributos a lo que manda el servidor y la tarjeta queda coherente sola.
   ═════════════════════════════════════════════════════════════════════════ */
function iniciarTarjetas() {
  /* Los anchos del srcset de la foto de la tarjeta (snippets/product-media.liquid) hasta 1920: una tarjeta
     no pide más que eso ni en una pantalla retina */
  const ANCHOS = [240, 352, 832, 1200, 1600, 1920];

  function conAncho(base, ancho) {
    try {
      const url = new URL(base, window.location.href);
      url.searchParams.set('width', String(ancho));
      return url.href;
    } catch (e) {
      return base;
    }
  }

  /* Las diapositivas visibles de la galería de la tarjeta, en orden. Las ocultas son las fotos de las otras
     variantes, que Horizon guarda escondidas. */
  function diapositivas(tarjeta) {
    const galeria = tarjeta.querySelector('slideshow-component');
    if (galeria && Array.isArray(galeria.slides)) return galeria.slides;
    return Array.from(tarjeta.querySelectorAll('slideshow-slide:not([hidden])'));
  }

  function imagenDe(diapositiva) {
    return diapositiva ? diapositiva.querySelector('img') : null;
  }

  /* La foto que se está viendo ahora (si se usaron las flechas de la tarjeta, no es la primera) */
  function imagenVisible(tarjeta) {
    const galeria = tarjeta.querySelector('slideshow-component');
    const lista = diapositivas(tarjeta);
    const actual = galeria && typeof galeria.current === 'number' ? galeria.current : 0;
    return imagenDe(lista[actual] || lista[0]);
  }

  /* Pone una foto (URL sin ancho) en un <img> de la galería. La primera vez guarda la original. */
  function ponerFoto(img, base) {
    if (!img || !base) return;
    if (img.dataset.tonaOriginal == null) {
      img.dataset.tonaOriginal = img.getAttribute('src') || '';
      img.dataset.tonaOriginalSet = img.getAttribute('srcset') || '';
    }
    const srcset = ANCHOS.map((a) => conAncho(base, a) + ' ' + a + 'w').join(', ');
    if (img.getAttribute('srcset') !== srcset) img.setAttribute('srcset', srcset);
    const src = conAncho(base, 832);
    if (img.getAttribute('src') !== src) img.setAttribute('src', src);
  }

  /* Vuelve a la foto de reposo: la del color elegido si hay uno, si no la original */
  function volverFoto(img) {
    if (!img || img.dataset.tonaOriginal == null) return;
    if (img.dataset.tonaElegida) {
      ponerFoto(img, img.dataset.tonaElegida);
      return;
    }
    img.setAttribute('src', img.dataset.tonaOriginal);
    if (img.dataset.tonaOriginalSet) img.setAttribute('srcset', img.dataset.tonaOriginalSet);
    else img.removeAttribute('srcset');
  }

  /* ── La foto del color al pasar el mouse por la pastilla ── */
  let previa = null; // el <img> que está mostrando la foto de una pastilla
  let timerVolver = 0;

  function pastillaDe(e) {
    return e.target instanceof Element ? e.target.closest('.product-card__color-swatch[data-swatch-hover-image]') : null;
  }

  document.addEventListener('mouseover', (e) => {
    const pastilla = pastillaDe(e);
    if (!pastilla) return;
    const tarjeta = pastilla.closest('product-card');
    if (!tarjeta) return;
    // «Foto del color al pasar el mouse», en Tona - Pastillas de color
    const contenedor = pastilla.closest('[data-color-swatches]');
    if (contenedor && contenedor.dataset.tonaHover === '0') return;
    const img = imagenVisible(tarjeta);
    if (!img) return;
    clearTimeout(timerVolver);
    if (previa && previa !== img) volverFoto(previa);
    previa = img;
    ponerFoto(img, pastilla.dataset.swatchHoverImage);
  });

  document.addEventListener('mouseout', (e) => {
    const pastilla = pastillaDe(e);
    if (!pastilla) return;
    if (e.relatedTarget instanceof Node && pastilla.contains(e.relatedTarget)) return;
    const img = previa;
    clearTimeout(timerVolver);
    // Un respiro: pasar de una pastilla a la de al lado cruza el hueco entre las dos, y sin esto la foto
    // parpadeaba a la original en el medio
    timerVolver = setTimeout(() => {
      if (img) volverFoto(img);
      if (previa === img) previa = null;
    }, 80);
  });

  /* El precio de la tarjeta pasa al del color elegido. La pastilla trae los textos ya formateados
     (data-tona-precio, data-tona-antes, data-tona-pct; solo si los precios del producto varían) y acá se
     cambian SOLO los números del precio que dibujó snippets/price.liquid, así se queda con su estilo.
     No se pide la tarjeta al servidor, como hace Horizon con sus pastillas: esa respuesta sale con el
     estilo de precio de la ficha y sin «oferta primero». Si el color no tiene precio anterior, el tachado
     y el % se esconden (medido el 18/09: ningún producto tiene un color en oferta y otro no). */
  function ponerPrecio(tarjeta, pastilla) {
    const precio = pastilla.dataset.tonaPrecio;
    if (!precio) return;
    const caja = tarjeta.querySelector("product-price [ref='priceContainer']");
    if (!caja) return;
    const enOferta = Boolean(pastilla.dataset.tonaAntes);
    const venta = caja.querySelector('.price');
    if (venta) {
      venta.textContent = precio;
      // El estilo de oferta (el recuadro o el verde de snippets/price.liquid) va solo si ese color está en oferta
      if (venta.dataset.tonaClase == null) venta.dataset.tonaClase = venta.className;
      venta.className = enOferta ? venta.dataset.tonaClase : venta.dataset.tonaClase.replace(/\btona-precio-\S+/g, '').trim();
    }
    // Con style y no con hidden: la etiqueta del % tiene su propio display y le ganaba al hidden (medido)
    const antes = caja.querySelector('.compare-at-price');
    if (antes) {
      if (enOferta) antes.textContent = pastilla.dataset.tonaAntes;
      (antes.closest('[role="group"]') || antes).style.display = enOferta ? '' : 'none';
    }
    const pct = caja.querySelector('.tona-precio-pct');
    if (pct) {
      if (pastilla.dataset.tonaPct) pct.textContent = pastilla.dataset.tonaPct;
      pct.style.display = pastilla.dataset.tonaPct ? '' : 'none';
    }
  }

  /* ── Tocar una pastilla con «Elegir el color» ── */
  function elegirColor(tarjeta, pastilla) {
    tarjeta.setAttribute('data-tona-color', pastilla.dataset.colorName || '');
    for (const otra of tarjeta.querySelectorAll('button.product-card__color-swatch')) {
      otra.setAttribute('aria-pressed', otra === pastilla ? 'true' : 'false');
    }

    const foto = pastilla.dataset.swatchHoverImage;
    if (foto) {
      const lista = diapositivas(tarjeta);
      const fotos = [foto, pastilla.dataset.tonaFotoSiguiente || foto];
      fotos.forEach((base, i) => {
        const img = imagenDe(lista[i]);
        if (!img) return;
        ponerFoto(img, base);
        img.dataset.tonaElegida = base;
        // La segunda se pide ya: es la que aparece al pasar el mouse por la foto
        if (i > 0) img.loading = 'eager';
      });
      const galeria = tarjeta.querySelector('slideshow-component');
      if (galeria && galeria.current > 0 && typeof galeria.select === 'function') {
        galeria.select(0, undefined, { animate: false });
      }
      // La transición a la ficha anima desde esta foto: si no coincide, Horizon la cambia por la de
      // arranque justo antes de irse (assets/product-card.js → #setImageSrcset)
      if (tarjeta.hasAttribute('data-featured-media-url')) {
        tarjeta.setAttribute('data-featured-media-url', conAncho(foto, 500));
      }
    }

    ponerPrecio(tarjeta, pastilla);

    // La tarjeta lleva a la ficha de ese color
    const url = pastilla.dataset.tonaUrl;
    if (url) {
      for (const a of tarjeta.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')) {
        a.setAttribute('href', url);
      }
    }
  }

  /* VIKA · Se expone para que la grilla de colección pueda dejar cada tarjeta en
     su color sin simular un click. Un click programático pasaría también por el
     listener de abajo, que en celular abre la fila de talles: al cargar la página
     se abrirían todas de golpe. */
  window.tonaElegirColor = elegirColor;

  document.addEventListener('click', (e) => {
    const pastilla = e.target instanceof Element ? e.target.closest('button.product-card__color-swatch') : null;
    if (!pastilla) return;
    const tarjeta = pastilla.closest('product-card');
    if (!tarjeta) return;
    e.preventDefault();
    elegirColor(tarjeta, pastilla);
    // PR-75: la fila pasa a los talles de ese color, y en celular aparece
    const fila = armarFila(tarjeta);
    if (fila && esTactil()) abrirFila(fila);
  });

  /* ═══ LOS TALLES SOBRE LA FOTO (PR-75) ═══
     Una fila de vidrio con los talles del color que muestra la tarjeta, como en EME Studios. Se arma con los
     datos del «+» de la misma tarjeta (data-tona-ar: talles, variantes y stock, ver el snippet) y se cuelga de
     .card-gallery, que es la caja exacta de la foto. En compu se ve con el :hover de la tarjeta (CSS); en
     celular, al tocar un color, con la clase tona-tf--abierta, y se cierra al tocar cualquier otra cosa.
     Ajustes: Ajustes del tema → «Tona - Agregado rápido» → Talles sobre la foto (llegan como data-tf-* en la
     ventana #tona-ar, y el estilo como variables --tona-tf-* en :root).
     Se arma recién cuando hace falta (el mouse entra a la tarjeta, el foco llega a ella o se toca un color):
     así también la tienen las tarjetas que llegan después por fetch. */

  let puntero = 'mouse'; // con qué se tocó la página la última vez: la fila se abre sola solo con el dedo
  const esTactil = () => puntero === 'touch' || puntero === 'pen' || window.matchMedia('(hover: none)').matches;

  function ajustesFila() {
    const ventana = document.getElementById('tona-ar');
    const ds = ventana ? ventana.dataset : {};
    return {
      prendida: ds.tf === '1',
      esconder: ds.tfAgotados === 'escondidos',
      rotulo: ds.tfRotulo || '',
      arriba: ds.tfPosicion === 'arriba',
      agotado: ds.textoAgotado || '',
      // «Tocar un talle lo agrega al carrito»: si no, abre el «+» con el color y el talle elegidos
      agregar: ds.tfAgregar !== '0',
      accion: ds.textoBoton || '',
    };
  }

  /* El color que muestra la tarjeta: el elegido o, si no se eligió, el de su foto principal */
  function colorVisible(tarjeta) {
    const elegido = tarjeta.getAttribute('data-tona-color');
    if (elegido) return elegido;
    const contenedor = tarjeta.querySelector('[data-color-swatches]');
    return contenedor ? contenedor.dataset.tonaColorInicial || '' : '';
  }

  /* Los talles de ese color, en el orden del producto. null si la fila no aplica: una sola variante, solo
     color, o más de una opción además del color (ahí un toque no alcanza para saber qué variante es, y queda
     el «+»). Un talle que no existe en ese color no está; uno sin stock, sí, con disponible = false. */
  function tallesDe(datos, color) {
    if (datos.variantes.length < 2) return null;
    const iColor = datos.opciones.findIndex((o) => o.esColor);
    const otras = datos.opciones.map((_, i) => i).filter((i) => i !== iColor);
    if (otras.length !== 1) return null;
    const k = otras[0];
    let deColor = color;
    if (iColor >= 0 && !deColor) {
      // Una prenda de un solo color no tiene pastillas ni color de arranque: va el de su primera variante
      const v = datos.variantes.find((x) => x.disponible) || datos.variantes[0];
      deColor = v.valores[iColor];
    }
    const talles = [];
    for (const valor of datos.opciones[k].valores) {
      const v = datos.variantes.find((x) => x.valores[k] === valor.nombre && (iColor < 0 || x.valores[iColor] === deColor));
      if (v) talles.push({ nombre: valor.nombre, id: v.id, disponible: v.disponible });
    }
    return { color: iColor >= 0 ? deColor : null, talles };
  }

  /* Arma la fila de una tarjeta, o la deja como está si ya muestra ese color. Devuelve la fila o null. */
  function armarFila(tarjeta) {
    const galeria = tarjeta.querySelector('.card-gallery');
    if (!galeria) return null;
    const cfg = ajustesFila();
    const fila = galeria.querySelector(':scope > .tona-tf');
    if (!cfg.prendida) {
      if (fila) fila.remove();
      return null;
    }
    const clave = colorVisible(tarjeta);
    if (fila && fila.dataset.clave === clave) return fila;
    // Ya se vio que en este color no hay fila: no se vuelve a leer el JSON en cada movimiento del mouse
    if (!fila && tarjeta.getAttribute('data-tona-tf-sin') === clave) return null;

    const mas = tarjeta.querySelector('.tona-ar-mas[data-tona-ar]');
    let datos = null;
    try {
      datos = mas ? leerDatos(mas) : null;
    } catch (e) {
      datos = null;
    }
    const resultado = datos ? tallesDe(datos, clave) : null;
    const talles = resultado ? resultado.talles.filter((t) => t.disponible || !cfg.esconder) : [];
    if (!talles.length) {
      if (fila) fila.remove();
      tarjeta.setAttribute('data-tona-tf-sin', clave);
      return null;
    }
    tarjeta.removeAttribute('data-tona-tf-sin');

    const nueva = document.createElement('div');
    nueva.className = 'tona-tf' + (cfg.arriba ? ' tona-tf--arriba' : '');
    if (fila && fila.classList.contains('tona-tf--abierta')) nueva.classList.add('tona-tf--abierta');
    nueva.dataset.clave = clave;
    if (resultado.color != null) nueva.dataset.color = resultado.color;
    nueva.setAttribute('role', 'group');
    nueva.setAttribute('aria-label', datos.titulo + (resultado.color ? ' · ' + resultado.color : ''));
    if (cfg.rotulo) {
      const rotulo = document.createElement('span');
      rotulo.className = 'tona-tf__rotulo';
      rotulo.setAttribute('aria-hidden', 'true');
      rotulo.textContent = cfg.rotulo;
      nueva.append(rotulo);
    }
    for (const t of talles) {
      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = 'tona-tf__talle';
      boton.textContent = t.nombre;
      boton.dataset.variante = String(t.id);
      boton.dataset.talle = t.nombre;
      if (!t.disponible) {
        boton.disabled = true;
        if (cfg.agotado) boton.setAttribute('aria-label', t.nombre + ' · ' + cfg.agotado);
      } else if (cfg.agregar && cfg.accion) {
        // «M · Agregar al carrito»: el lector de pantalla dice qué hace el toque, con el texto del botón del +
        boton.setAttribute('aria-label', t.nombre + ' · ' + cfg.accion);
      }
      nueva.append(boton);
    }
    if (fila) fila.replaceWith(nueva);
    else galeria.append(nueva);
    return nueva;
  }

  function abrirFila(fila) {
    for (const otra of document.querySelectorAll('.tona-tf--abierta')) {
      if (otra !== fila) otra.classList.remove('tona-tf--abierta');
    }
    fila.classList.add('tona-tf--abierta');
  }

  /* Tocar afuera cierra la fila abierta. «Afuera» es todo menos la fila y las pastillas de esa tarjeta. */
  function cerrarFilas(objetivo) {
    const abiertas = document.querySelectorAll('.tona-tf--abierta');
    if (!abiertas.length) return;
    for (const fila of abiertas) {
      const tarjeta = fila.closest('product-card');
      const pastillas = tarjeta ? tarjeta.querySelector('[data-color-swatches]') : null;
      if (objetivo && (fila.contains(objetivo) || (pastillas && pastillas.contains(objetivo)))) continue;
      fila.classList.remove('tona-tf--abierta');
    }
  }

  document.addEventListener(
    'pointerdown',
    (e) => {
      puntero = e.pointerType || 'mouse';
      cerrarFilas(e.target instanceof Element ? e.target : null);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const tarjeta = e.target instanceof Element ? e.target.closest('product-card') : null;
      if (tarjeta) armarFila(tarjeta);
    },
    { passive: true }
  );

  document.addEventListener('focusin', (e) => {
    const tarjeta = e.target instanceof Element ? e.target.closest('product-card') : null;
    if (tarjeta) armarFila(tarjeta);
  });

  /* Tocar un talle lo agrega al carrito sin abrir nada, como EME («Añadir» + los talles). El carrito se entera
     por el mismo camino que el «+» (agregarAlCarrito → CartAddEvent) y después pasa lo que diga «Qué pasa
     después de agregar»: el aviso o el carrito lateral. Con «Tocar un talle lo agrega al carrito» apagado,
     abre el «+» con el color y el talle ya elegidos. */
  async function agregarDesdeLaFila(fila, boton, tarjeta) {
    if (fila.getAttribute('aria-busy') === 'true') return;
    fila.setAttribute('aria-busy', 'true');
    try {
      await agregarAlCarrito(Number(boton.dataset.variante), tarjeta.getAttribute('data-product-id') || '', tarjeta);
      fila.classList.remove('tona-tf--abierta');
      despuesDeAgregar();
    } catch (err) {
      console.warn('Tona - Talles sobre la foto: no se pudo agregar', err);
      const error = document.querySelector('#tona-ar .tona-ar__error');
      mostrarToast(error ? error.textContent.trim() : '');
    } finally {
      fila.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const boton = e.target instanceof Element ? e.target.closest('.tona-tf__talle') : null;
    if (!boton) return;
    e.preventDefault();
    const fila = boton.closest('.tona-tf');
    const tarjeta = boton.closest('product-card');
    const mas = tarjeta ? tarjeta.querySelector('.tona-ar-mas[data-tona-ar]') : null;
    if (!fila || !tarjeta || !mas || boton.disabled) return;
    if (ajustesFila().agregar) {
      agregarDesdeLaFila(fila, boton, tarjeta);
      return;
    }
    fila.classList.remove('tona-tf--abierta');
    if (abrirVentana) abrirVentana(mas, { color: fila.dataset.color || null, talle: boton.dataset.talle });
  });
}
